import { ManagerAndDirectReportsAppsQuery } from '../../apps/ManagerAndDirectReportsAppsQuery';
import { UniversalWorklistQuery } from '../../home/UniversalWorklistQuery';
import { useContext, useEffect, useState } from 'react';
import { Workbox } from '../Workbox';
import { Progress } from '@backstage/core-components';
import {
  DevHubAlert,
  DevHubBox,
  DevHubChip,
  DevHubColorProps,
  DevHubDynamicIcon,
  DevHubGrid,
  DevHubLink,
  DevHubTooltip,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { useLazyQuery } from '@apollo/client';
import { UserContext } from '../../../contexts/UserContext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { DevHubIconButton } from '@devxdiscover/devhub-ui';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';
import { makeStyles } from '@material-ui/core/styles';
import BulkExportResourcesReport from '../../insights/BulkExportResourcesReport';

export type Severity = 'success' | 'info' | 'warning' | 'error';

/**
 * Application interface for strong typing
 */
interface AppItem {
  applicationId: number;
  name: string;
  status: string;
  roles: Array<{ displayName: string }>;
  owner: { displayName: string };
  createdAt: string;
  resources: Array<Record<string, unknown>>;
  updatedAt?: string;
  isStarred?: boolean;
  hasWorklistItems?: boolean;
}

/**
 * CSS styles for clickable rows
 */
const useStyles = makeStyles({
  clickableRow: {
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
});

/**
 * ExecutiveAppView component is used to display the apps owned by the user.
 */
export const ExecutiveAppView = () => {
  const classes = useStyles();
  const title = 'My Applications';
  const { login } = useContext(UserContext);
  const [applications, setApplications] = useState<AppItem[]>([]);
  const [appsWithWorklistItems, setAppsWithWorklistItems] = useState<
    Set<number>
  >(new Set());
  const [selectedApps, setSelectedApps] = useState<AppItem[]>([]);
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  const handleAlert = ({ open, message, severity }: any) => {
    setAlert({ open, message, severity });
  };

  const [fetchApplications, { data, loading, error }] = useLazyQuery(
    ManagerAndDirectReportsAppsQuery,
    {
      errorPolicy: 'all',
      variables: {
        login: login,
        pagination: {
          limit: 100,
          offset: 0,
        },
      },
    },
  );

  const [fetchWorklistItems, { data: worklistData }] = useLazyQuery(
    UniversalWorklistQuery,
    {
      errorPolicy: 'all',
      variables: {
        filter: {
          login: login,
          statuses: ['OPEN', 'IN_PROGRESS'],
        },
      },
    },
  );

  const handleStarChange = (appId: number, isStarred: boolean) => {
    const starredAppsString = localStorage.getItem('starredApplications');
    const starredApps = starredAppsString ? JSON.parse(starredAppsString) : [];

    if (isStarred && !starredApps.includes(appId)) {
      starredApps.push(appId);
    } else if (!isStarred && starredApps.includes(appId)) {
      const index = starredApps.indexOf(appId);
      starredApps.splice(index, 1);
    }

    localStorage.setItem('starredApplications', JSON.stringify(starredApps));

    setApplications(prevApps =>
      prevApps.map((app: AppItem) =>
        app.applicationId === appId ? { ...app, isStarred } : app,
      ),
    );
  };

  const AppItemStarToggle = ({ app }: { app: AppItem }) => {
    return (
      <DevHubIconButton
        onClick={(e: {
          preventDefault: () => void;
          stopPropagation: () => void;
        }) => {
          e.preventDefault();
          e.stopPropagation();
          handleStarChange(app.applicationId, !app.isStarred);
        }}
      >
        <DevHubDynamicIcon
          iconName="StarSharp"
          fontSize="medium"
          color={app.isStarred ? 'gold' : 'disabled'}
        />
      </DevHubIconButton>
    );
  };

  useEffect(() => {
    if (data) {
      const fetchedApplications =
        data?.identity?.user?.resources?.applicationPortfolio?.applications ||
        [];

      const filteredApplications = fetchedApplications.filter(
        (app: Pick<AppItem, 'status'>) => {
          const status = (app.status || '').toLowerCase();
          return status === 'active' || status === 'pre-prod';
        },
      );

      const starredAppsString = localStorage.getItem('starredApplications');
      const loadedStarredIds = starredAppsString
        ? JSON.parse(starredAppsString)
        : [];

      const processedApps = filteredApplications.map(
        (app: { applicationId: string | number } & Partial<AppItem>) => {
          const appId =
            typeof app.applicationId === 'string'
              ? parseInt(app.applicationId, 10)
              : app.applicationId;

          const hasWorklistItems = appsWithWorklistItems.has(appId);

          return {
            ...app,
            applicationId: appId,
            isStarred: loadedStarredIds.includes(appId),
            hasWorklistItems: hasWorklistItems,
          };
        },
      );

      setApplications(processedApps);
    }
  }, [data, appsWithWorklistItems]);

  const isWorklistEnabled = ConfigHelper.instance.isFeatureEnabled(
    Feature.UNIVERSAL_WORKLIST,
  );

  useEffect(() => {
    if (isWorklistEnabled) {
      fetchWorklistItems();
    }
  }, [fetchWorklistItems, isWorklistEnabled]);

  useEffect(() => {
    if (worklistData && isWorklistEnabled) {
      const worklistItems =
        worklistData?.devhub?.universalWorklist?.worklistItemsPaged
          ?.worklistItems || [];
      const appIds = new Set<number>();

      worklistItems.forEach(
        (item: {
          assignedTo:
            | Array<{
                __typename: string;
                applications?: Array<{ applicationId: number }>;
              }>
            | {
                __typename: string;
                applications?: Array<{ applicationId: number }>;
              };
        }) => {
          if (item.assignedTo) {
            if (Array.isArray(item.assignedTo)) {
              item.assignedTo.forEach(
                (assignee: {
                  __typename: string;
                  applications?: Array<{ applicationId: number }>;
                }) => {
                  if (
                    assignee.__typename === 'UwlWorklistRoleAssignment' &&
                    assignee.applications
                  ) {
                    assignee.applications.forEach(
                      (app: { applicationId: number }) => {
                        if (app.applicationId) {
                          appIds.add(app.applicationId);
                        }
                      },
                    );
                  }
                },
              );
            } else if (
              item.assignedTo.__typename === 'UwlWorklistRoleAssignment' &&
              item.assignedTo.applications
            ) {
              item.assignedTo.applications.forEach(
                (app: { applicationId: number }) => {
                  if (app.applicationId) {
                    appIds.add(app.applicationId);
                  }
                },
              );
            }
          }
        },
      );

      setAppsWithWorklistItems(appIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worklistData]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  return (
    <Workbox
      color="blue"
      title={title}
      info=""
      component="ExecutiveAppView"
      subHeader="Applications that you or your direct reports manage"
    >
      <>
        {loading && <Progress />}
        {error && (
          <DevHubAlert severity="error">
            Error fetching applications: {error.message}
          </DevHubAlert>
        )}
        {!loading && !error && applications.length === 0 && (
          <DevHubAlert severity="info">
            No applications found with status Active or Pre-Prod.
          </DevHubAlert>
        )}
        {!loading && !error && applications.length > 0 && (
          <>
            <DevHubBox mb={2}>
              <DevHubGrid container spacing={2} alignItems="center">
                <DevHubGrid item xs>
                  <DevHubTypography variant="body2" color="textSecondary">
                    {selectedApps.length > 0
                      ? `${selectedApps.length} application(s) selected`
                      : 'Select applications to export resource reports'}
                  </DevHubTypography>
                </DevHubGrid>
                <DevHubGrid item>
                  <BulkExportResourcesReport
                    appIds={selectedApps.map(app => app.applicationId)}
                    handleAlert={handleAlert}
                  />
                </DevHubGrid>
              </DevHubGrid>
            </DevHubBox>
            <DataTable
              value={applications}
              selection={selectedApps}
              onSelectionChange={e => setSelectedApps(e.value as AppItem[])}
              dataKey="applicationId"
              selectionMode="checkbox"
              paginator
              rows={10}
              rowsPerPageOptions={[5, 10, 25, 50]}
              tableStyle={{ minWidth: '50rem', fontSize: '0.875rem' }}
              sortField="name"
              sortOrder={1}
              removableSort
              size="small"
              rowClassName={() => classes.clickableRow}
              onRowClick={e => {
                // Only handle row clicks if they didn't click on buttons/links with their own handlers
                if (
                  !(e.originalEvent.target as Element).closest(
                    'button, a, .p-clickable',
                  )
                ) {
                  window.open(`/app/id/${e.data.applicationId}`, '_blank');
                }
              }}
            >
              <Column
                selectionMode="multiple"
                headerStyle={{ width: '3rem' }}
                exportable={false}
              />
              <Column
                field="applicationId"
                header="App ID"
                sortable
                style={{ width: '90px' }}
              />
              <Column
                field="name"
                header="Name"
                sortable
                style={{ width: '25%' }}
              />
              <Column
                field="owner.displayName"
                header="Owner"
                sortable
                body={(rowData: AppItem) =>
                  rowData.owner?.displayName || 'Unknown'
                }
              />
              <Column
                field="updatedAt"
                header="Updated"
                sortable
                style={{ width: '100px' }}
                body={(rowData: AppItem) => {
                  const date = rowData.updatedAt
                    ? new Date(rowData.updatedAt)
                    : null;
                  return date ? date.toLocaleDateString() : 'Unavailable';
                }}
              />
              {isWorklistEnabled && (
                <Column
                  field="hasWorklistItems"
                  header="Tasks"
                  sortable
                  sortFunction={event => {
                    const { data: sortData, order } = event;
                    return sortData.sort((a: AppItem, b: AppItem) => {
                      const valueA = a.hasWorklistItems || false;
                      const valueB = b.hasWorklistItems || false;

                      if (valueA === valueB) {
                        return 0;
                      }

                      const result = valueA ? -1 : 1;
                      return order === 1 ? result : result * -1;
                    });
                  }}
                  style={{ width: '50px', textAlign: 'center' }}
                  headerStyle={{ textAlign: 'center' }}
                  body={(rowData: AppItem) => {
                    if (rowData.hasWorklistItems) {
                      return (
                        <DevHubTooltip title="This application has pending tasks in the worklist">
                          <DevHubLink href="/action-center/worklist">
                            <DevHubIconButton>
                              <DevHubDynamicIcon
                                iconName="Assignment"
                                fontSize="medium"
                                color="error"
                              />
                            </DevHubIconButton>
                          </DevHubLink>
                        </DevHubTooltip>
                      );
                    }
                    return (
                      <DevHubTooltip title="No pending tasks for this application">
                        <DevHubIconButton disabled>
                          <DevHubDynamicIcon
                            iconName="CheckCircleOutline"
                            fontSize="medium"
                            color="success"
                          />
                        </DevHubIconButton>
                      </DevHubTooltip>
                    );
                  }}
                />
              )}

              <Column
                field="status"
                header="Status"
                sortable
                style={{
                  width: '100px',
                  textAlign: 'center',
                  verticalAlign: 'center',
                }}
                body={(rowData: AppItem) => {
                  const status = rowData.status || 'Unknown';
                  const statusLower = status.toLowerCase();

                  let chipColor: DevHubColorProps = 'default';
                  if (statusLower === 'active') {
                    chipColor = 'green';
                  } else if (statusLower === 'pre-prod') {
                    chipColor = 'blue';
                  }

                  return (
                    <DevHubChip
                      label={status}
                      chipColor={chipColor}
                      chipSize="small"
                    />
                  );
                }}
              />
              <Column
                field="isStarred"
                header="Star"
                sortable
                sortFunction={event => {
                  const { data: sortData, order } = event;
                  return sortData.sort((a: AppItem, b: AppItem) => {
                    const valueA = a.isStarred || false;
                    const valueB = b.isStarred || false;

                    if (valueA === valueB) {
                      return 0;
                    }

                    const result = valueA ? -1 : 1;
                    return order === 1 ? result : result * -1;
                  });
                }}
                body={(rowData: AppItem) => <AppItemStarToggle app={rowData} />}
                style={{ width: '60px', alignContent: 'center' }}
              />
              <Column
                header="Resources"
                body={(rowData: AppItem) => {
                  // Determine which resources tab to link to based on features
                  const hasCloudAccounts =
                    ConfigHelper.instance.isFeatureEnabled(
                      Feature.CLOUD_ACCOUNTS,
                    );
                  const resourcesPath = hasCloudAccounts
                    ? 'resources/cloud-accounts'
                    : 'resources/devices';

                  return (
                    <DevHubTooltip title="View application resources">
                      <DevHubLink
                        href={`/catalog/default/system/${rowData.applicationId}/${resourcesPath}`}
                      >
                        <DevHubIconButton size="medium">
                          <DevHubDynamicIcon
                            iconName="OpenInNew"
                            fontSize="medium"
                          />
                        </DevHubIconButton>
                      </DevHubLink>
                    </DevHubTooltip>
                  );
                }}
                style={{ width: '100px', textAlign: 'center' }}
                headerStyle={{ textAlign: 'center' }}
              />
            </DataTable>
          </>
        )}
        <DevHubBox mt={2}>
          {alert.open && (
            <DevHubAlert
              variant="outlined"
              severity={alert.severity as Severity}
              onClose={() => setAlert({ ...alert, open: false })}
            >
              {alert.message}
            </DevHubAlert>
          )}
        </DevHubBox>
      </>
    </Workbox>
  );
};
